import AutoSuggest from './AutoSuggest';
import Category from './Category';
import Result from './Result';
import History from './History';

export default class Filter {
	constructor(data, config) {
		try {
			this.data = data;
			this.results = new Result(config.resultsContainer, data, {});
			this.search = undefined;
			this.history = new History();

			this.selectors = {
					resetFilterSelector: config?.selectors?.categoryTreeSelector || '.js-dc-reset-filter',
					applyFilterSelector: config?.selectors?.categoryTreeSelector || '.js-dc-apply-filter',
					searchInputSelector: config?.selectors?.searchInputSelector || '.js-dc-filter-search',
					categoryTreeSelector: config?.selectors?.categoryTreeSelector || '.js-dc-category-tree',
					categoryGroupSelector: config?.selectors?.categoryGroupSelector || '.js-dc-category-group',
					categoryItemSelector: config?.selectors?.categoryItemSelector || '.js-dc-category-group-item'
				};
			this.config = {
					activeClass: 'selected',
					hiddenClass: 'hidden',
					activeItems: {},  // holds all currently active category objects
					...config
				};
			this.elements = {
					container: config.container || undefined,
					searchInput: config.container.querySelector(this.selectors.searchInputSelector),
					resetFilterButton: config.container.querySelector(this.selectors.resetFilterSelector),
					applyFilterButton: config.container.querySelector(this.selectors.applyFilterSelector),
					scrollTarget: undefined,
					tree: undefined,  // the container of all categories
					groups: undefined,  // the groups of categories
					categories: [],  //  all categories among all groups, a category remembers the group it belongs to via index position
				};
			if (this.elements.applyFilterButton?.hasAttribute('data-scroll-target')) {
				this.elements.scrollTarget = document.querySelector(this.elements.applyFilterButton.getAttribute('data-scroll-target'));
			}

			this.#collectCategoryGroups();
			if (this.elements.resetFilterButton) {
				this.elements.resetFilterButton.addEventListener('click', this.#resetFilter.bind(this));
				this.#hideFilterResetButton();
			}
			if (this.elements.applyFilterButton) {
				this.elements.applyFilterButton.addEventListener('click', this.#applyFilter.bind(this));
			}
			if (this.elements.searchInput) {
				this.search = new AutoSuggest({
					input: this.elements.searchInput, 
					files: this.data.files,
					onChange: this.#applySearch.bind(this),
				});
			}

			let hash = this.history.getHash();
			this.applyHash(hash);
			// Listen for changes
			this.history.onChange(newHash => this.updateHash(newHash));
		} catch (e) {
			console.error(e);
		}
	}

	/**
	 * Applies current hash.
	 * 
	 * @param {string} hash 	The has to apply.
	 * @returns 
	 */
	applyHash(hash) {
		if (!hash.length) {
			return;
		}

		let mapCategories = {};
		this.elements.categories.forEach(cat => {
			mapCategories[ cat.getUid() ] = cat;
		});

		let aCats = this.history.getActiveCategories();
		Object.keys(aCats).forEach(groupIndex => {
			this.config.activeItems[groupIndex] = this.config.activeItems[groupIndex] || [];

			aCats[groupIndex].forEach(catId => {
				if (typeof(mapCategories[catId]) !== 'undefined' && mapCategories[catId].isAvailable()) {
					this.config.activeItems[groupIndex].push(mapCategories[catId]);
					mapCategories[catId].setActive(true);
				}
			});

			// if only invalid ids were provided and we have an empty groupIndex, remove it again
			if (this.config.activeItems[groupIndex].length === 0) {
				delete this.config.activeItems[groupIndex];
			}
		});

		let search = this.history.getSearch();
		if (this.search && search.length > 0) {
			let bPoints = {};
			let d = +new Date();
			search.forEach((s, i) => bPoints[d+i] = s);
			this.search.setBulletPoints(bPoints);
		}

		this.#applyFilter(false);  // do not use applySearch, that resets activeItems in URL and category tree
	}

	/**
	 * Udate hash. Only update, if hash consists of valid categories and/or search.
	 * 
	 * @param {string} newHash	The new hash to apply if valid.
	 */
	updateHash(newHash) {
		if (!newHash.length) {
			return;
		}

		let testedHash = this.history.testHash(newHash);
		// only if any kind of change has been made that is valid, update the internals
		if (testedHash.search.length > 0 || Object.keys(testedHash.categories).length > 0) {
			this.#resetFilter();
			this.history.disassembleString(newHash);
			this.applyHash(newHash);
		} else {
			this.#resetFilter(false);
		}
	}

	/**
	 * Filter class bound event handler of a Category.
	 * 
	 * @param {Category} clickedCategory The Category that was clicked.
	 */
	updateFilter(clickedCategory) {
		if (!clickedCategory.isActive()) {
			let cuid = clickedCategory.getUid();

			this.config.activeItems[ clickedCategory.getGroupIndex() ] = this.config.activeItems[ clickedCategory.getGroupIndex() ]?.filter(cat => cat.getUid() !== cuid);
			if (this.config.activeItems[ clickedCategory.getGroupIndex() ] === undefined || this.config.activeItems[ clickedCategory.getGroupIndex() ].length === 0) {
				delete this.config.activeItems[ clickedCategory.getGroupIndex() ];

				if (this.config.activeItems === undefined) {
					this.config.activeItems = {};
				}
			}
		} else {
			this.config.activeItems[ clickedCategory.getGroupIndex() ] = this.config.activeItems[ clickedCategory.getGroupIndex() ] || [];
			this.config.activeItems[ clickedCategory.getGroupIndex() ].push(clickedCategory);
		}

		if (JSON.stringify(this.config.activeItems) === '{}') {
			this.#hideFilterResetButton();
		} else {
			this.#showFilterResetButton();
		}

		this.#applyFilter(false);
	}

	#showFilterResetButton() {
		if (this.elements.resetFilterButton) {
			this.elements.resetFilterButton.classList.remove(this.config.hiddenClass);
		}
	}

	#hideFilterResetButton() {
		if (this.elements.resetFilterButton) {
			this.elements.resetFilterButton.classList.add(this.config.hiddenClass);
		}
	}

	/**
	 * Resets all filter settings via click on reset button.
	 */
	#resetFilter(resetHistory = true) {
		let groups = Object.keys(this.config.activeItems);
		groups.forEach(group => {
			this.config.activeItems[group].forEach(cat => {
				if (!cat.isDisabled()) {
					cat.setActive(false);
				}
			});
		});

		// Enable cats again
		this.elements.categories.forEach(cat => {
			cat.enable();
		});

		this.config.activeItems = {};
		this.results.showAll();
		this.search && this.search.clearSearch();
		this.#hideFilterResetButton();
		if (resetHistory !== false) {
			this.history.reset();
		}
	}

	/**
	 * Enables/Disables all categories in the category tree.
	 * 
	 * @param {Object} usedCategories A map of categories used among all visible files, format: [catUid => true].
	 */
	#updateCategoryDisplay(usedCategories) {
		let removedCats = {};
		this.elements.categories.forEach(cat => {
			if (usedCategories[ cat.getUid() ]) {
				cat.enable();
			} else {
				cat.disable();
				removedCats[ cat.getUid() ] = true;
			}
		});

		// remove categories from active items, if some were disabled in the process above that were selected
		Object.keys(this.config.activeItems).forEach(group => {
			this.config.activeItems[group] = this.config.activeItems[group].filter(cat => !removedCats[ cat.getUid() ]);
			if (this.config.activeItems[group].length === 0) {
				delete this.config.activeItems[group];
			}
		});
		if (Object.keys(usedCategories) === 0 || this.config.activeItems === undefined) {
			this.config.activeItems = {};
		}
	}

	/**
	 * Triggers the filter process, by updating the category tree, search and the results.
	 * 
	 * Invoked by "Show result"-button, Category click and search input changes.
	 */
	#applyFilter(scroll = true) {
		let search = this.search?.getBulletPoints() || {};
		if (JSON.stringify(this.config.activeItems) !== '{}' || Object.keys(search).length > 0) {
			let usedCategories = this.results.show(this.config.activeItems, this.search?.getBulletPoints());
			this.#showFilterResetButton();

			// if only the search input is used, we have no categories used (yet)
			if (Object.keys(usedCategories).length > 0) {
				this.#updateCategoryDisplay(usedCategories);
			} else {
				let aCats = this.results.getAllowedCategories();
				this.#updateCategoryDisplay(aCats);

				if (Object.keys(search).length === 0) {
					this.#hideFilterResetButton();
				}
			}

			// History state changes
			//? Convert { {groupIndex}: [ {Category} ] }  into  { {groupIndex}: [ {uid} ] }
			let groups = Object.keys(this.config.activeItems);
			let mapGroups = {};
			groups.forEach(groupIndex => {
				this.config.activeItems[groupIndex].forEach(cat => {
					let catUid = cat.getUid();

					mapGroups[groupIndex] = mapGroups[groupIndex] || [];
					mapGroups[groupIndex].push(catUid);
				});
			});
			if (Object.keys(search).length === 0) {
				this.history.setSearchAndCategories([], mapGroups);
			} else {
				this.history.setSearchAndCategories(
						Object.keys(search).reduce((acc, key) => {
							acc.push(search[key]);
							return acc;
						}, []), 
						mapGroups
					);
			}
		} else {
			this.elements.categories.forEach(cat => {
				cat.enable();
			});
			this.results.showAll();
			this.#hideFilterResetButton();
			this.history.reset();
		}

		// Scroll to result list
		if (scroll && this.elements.scrollTarget) {
			this.elements.scrollTarget.scrollIntoView({
				behavior: 'smooth',
				block: 'start'
			});
		}
	}

	/**
	 * Using the AutoSuggest search and selecting an item will get us here.
	 * 
	 * @param {boolean} scroll 
	 */
	#applySearch(scroll = false) {
		let activeItemsBeforeSearch = JSON.stringify(this.config.activeItems);
		this.#applyFilter(scroll);

		/**
		 *! When selecting a category and AFTERWARDS using the search, it can happen that:
		 * all categories are grayed out, search is displayed BUT no results are shown.
		 * 
		 * Reason:
		 * Not valid categories are grayed out automatically and during that removed from search criteria. 
		 * The search input is considered like a category and disables not valid categories 
		 * as well but search input is never disabled. If the search input disabled a previously valid category, 
		 * the files need to be searched through again without the previous active category. 
		 * 
		 * So we invoke manually again, if activeItems changed, ie a category was removed while a search is present.
		 */
		if (JSON.stringify(this.config.activeItems) !== activeItemsBeforeSearch && Object.keys(search).length > 0) {
			this.#applyFilter(false);
		}
	}

	/**
	 * Collects all categories visible in this filter.
	 * Disabled non-used categories, creates Category items that handle events.
	 */
	#collectCategoryGroups() {
		this.elements.tree = this.elements.container.querySelector(this.selectors.categoryTreeSelector);
		if (this.elements.tree) {
			this.elements.groups = this.elements.tree.querySelectorAll(this.selectors.categoryGroupSelector);
			this.elements.groups.forEach((group, i) => {
				const categories = group.parentNode.querySelectorAll(this.selectors.categoryItemSelector);

				categories.forEach(category => {
					let cat = new Category(category, { 
						groupIndex: i, 
						unavailable: this.data.disabledCategories[category.getAttribute('data-uid')] === true, 
						onClick: this.updateFilter.bind(this)
					});

					this.elements.categories.push(cat);
				});
			});
		}
	}
}
